import React, { useRef } from 'react';
import { getApiUrl } from '../../config/apiConfig';

interface ResumeSnapshotCardProps {
  resumeFile: File | null;
  setResumeFile: React.Dispatch<React.SetStateAction<File | null>>;
  resumeData: any;
  setResumeData: React.Dispatch<React.SetStateAction<any>>;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadError: string | null;
  setUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  fileError: string;
  setFileError: React.Dispatch<React.SetStateAction<string>>;
  copySuccess: {[key: string]: string};
  setCopySuccess: React.Dispatch<React.SetStateAction<{[key: string]: string}>>;
  processingDone: {[key: string]: boolean};
  setProcessingDone: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | null>>) => void;
  copyToClipboard: (text: string, type: string) => void;
  clearForm: (fileType: string) => void;
}

const ResumeSnapshotCard: React.FC<ResumeSnapshotCardProps> = ({
  resumeFile,
  setResumeFile,
  resumeData,
  setResumeData,
  isUploading,
  setIsUploading,
  uploadError,
  setUploadError,
  fileError,
  copySuccess,
  processingDone,
  handleFileChange,
  copyToClipboard,
  clearForm
}) => {
  const resumeFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async () => {
    if (!resumeFile) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Use the API config to get the correct endpoint
      const apiUrl = getApiUrl('RESUME_SNAPSHOT');
      
      const formData = new FormData();
      formData.append('file', resumeFile);
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setResumeData(data);
      } catch (fetchError) {
        // Check if it's a network error (connection refused)
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          throw new Error('Backend server is not running. Please start the backend server on port 5275.');
        } else {
          throw fetchError;
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-4 dark:text-white">Resume Snapshot</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Instantly parse resume text into a clean, structured profile for HR.
      </p>
      
      <div className="mb-6 border dark:border-gray-700 p-4 rounded-md bg-white dark:bg-gray-800">
        <h3 className="font-medium mb-2 dark:text-white">Upload Resume</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Upload your resume to get a structured summary</p>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center">
            <label htmlFor="resume-file" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 py-2 px-4 rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors">
              Choose File
            </label>
            <span className="ml-3 text-gray-500 dark:text-gray-400 text-sm">{resumeFile ? resumeFile.name : 'No file chosen'}</span>
            <input id="resume-file" type="file" ref={resumeFileInputRef} onChange={(e) => handleFileChange(e, setResumeFile)} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" />
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handleFileUpload} disabled={!resumeFile || isUploading} className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed flex items-center">
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Process Resume'
              )}
            </button>
            <button onClick={() => clearForm('resume')} disabled={!resumeFile && !resumeData} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed">
              Clear
            </button>
          </div>
        </div>
        {fileError && <p className="text-red-500 text-sm mt-2">{fileError}</p>}
        {uploadError && <p className="text-red-500 text-sm mt-2">{uploadError}</p>}
      </div>
      
      {/* Resume Results - Plain JSON Display */}
      {resumeData && (
        <div className="mb-6 border dark:border-gray-700 p-4 rounded-md bg-white dark:bg-gray-700">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium dark:text-white">Resume Snapshot Results (JSON)</h3>
            <button 
              onClick={() => {
                // Format the data as plain JSON
                const jsonContent = JSON.stringify(resumeData, null, 2);
                copyToClipboard(jsonContent, 'resume');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"></path>
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"></path>
              </svg>
              {copySuccess.resume || 'Copy JSON'}
            </button>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-300 whitespace-pre-wrap">
              {JSON.stringify(resumeData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeSnapshotCard;
